import NextHead from "next/head";
import { useCallback, useContext, useEffect, useRef } from "react";
import { MapContainer, ZoomControl } from "react-leaflet";

import { useTranslator } from "@eo-locale/react";

import Cookies from "js-cookie";

import { isEmpty } from "lodash";

import { useAppSelector } from "@/redux/store/hooks";
import { Box, useMediaQuery } from "@/storybook-components";
import { EsriContext } from "../context/esri-context";
import { EsriContextType } from "../context/esri-context-type";
import CustomMarkers from "./markers/custom-markers";
import Layers from "./plugins/layers";
import { LocateControl } from "./plugins/locate-control";
import MarkerClusterGroup from "./plugins/markerCluster";
import EsriLeafletGeoSearchWithFocus from "./plugins/search";
import VectorBasemapLayer from "./plugins/vectorBaseMapLayer";

import { esriToken, languages } from "../constants";
import NewMarker from "./markers/new-marker";
import SetViewOnClick from "./set-view-on-click";

import AllAlert from "./access-logic/all-places-alerts";
import pinDetails from "./pin-details";
import { addAssetPrefix } from "@/helpers/add-asset-prefix";
import useEmbeds from "@/hooks/useEmbeds";
interface IEsriMap {
  isLoading: boolean;
  viewMarkerClickHandler: (data: any, cardDetails: string) => void;
  viewPinModalProps: any;
}

const EsriMap = ({ isLoading, viewMarkerClickHandler }: IEsriMap) => {
  const {
    style: mapStyle,
    zoom,
    lat,
    lng
  } = useAppSelector((state) => state.place.place);
  const viewPinDetails = useAppSelector((state) => state.place.pinDetails);

  const marker = useAppSelector((state) => state.placesMarkerSlice.marker);

  const currentMapBounds = useAppSelector(
    (state) => state.place.currentMapBounds
  );
  const { setMapRef, showInteractions, mapRef, openInfoModal } = useContext(
    EsriContext
  ) as EsriContextType;

  const pins = useAppSelector((state) => state.placeMarkersSlice.markers);
  const { translate } = useTranslator();
  const { locale } = useAppSelector((state) => state.site.site);
  const basemapLayerRef = useRef();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { nonce } = useAppSelector((state) => state.adapter.adapter);
  const { googleTranslationEnabled } = useAppSelector(
    (state) => state.settings.settings
  );
  const { isEmbed } = useEmbeds();
  const { isEditMode } = useAppSelector((state) => state.placesMarkerSlice);
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const pinSearchFromUrl = useAppSelector(
    (state) => state.placesRightPanelView.pinSearchFromUrl
  );
  const geosearchControlRef = useRef();
  const { login } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );
  const heightOfMap = login ? "135px" : "127px";
  const navbarElement = document.getElementsByClassName(
    "ehq-participant-header"
  )[0];

  const mapComponent = useCallback(() => {
    const mapContainerStyle = {
      height: navbarElement
        ? isMobile
          ? "100vh"
          : `calc(100vh - ${heightOfMap})`
        : isMobile
          ? googleTranslationEnabled
            ? `calc(100svh - 2.5rem)`
            : "100svh"
          : "100vh",
      width: "100%"
    };

    const updateMapStyle = mapStyle?.replace("-", "/");
    const mapName = mapStyle?.includes("-standard")
      ? updateMapStyle.replace("-standard", "/standard")
      : updateMapStyle;

    useEffect(() => {
      const map = mapRef?.current?.leafletElement;
      map?.fitBounds(currentMapBounds);
    }, [mapRef]);

    const { cookiePolicy } = useAppSelector((state) => state.features.features);

    let isCookiePolicyEnabled =
      cookiePolicy?.enabled && isEmpty(Cookies.get("cookie_consent"));
    const hasCookieConsent = useAppSelector(
      (state) => state.cookieConsent.hasCookieConsent
    );

    const pinDetails = useAppSelector((state) => state.place.pinDetails);
    useEffect(() => {
      // check if the cookie present
      // if the cookie is  present  -> consent is given -> show the modal
      // if the new cookie consent manager is not enabled -> no cookie
      const cookie = Cookies.get("cookie_consent");

      if (cookie || hasCookieConsent || !isCookiePolicyEnabled) {
        openInfoModal();
      }
    }, [hasCookieConsent]);

    const isEmbedLayout = isEmbed ? "embed" : "";
    const isGoogleTranslateEnabled = googleTranslationEnabled
      ? "google-translate-enabled"
      : "";

    return (
      <>
        <NextHead>
          <link
            nonce={nonce}
            rel="stylesheet"
            href={addAssetPrefix("/external/css/leaflet@1.9.4.css")}
          />
          <link
            nonce={nonce}
            href={addAssetPrefix("/external/css/maplibre-gl@4.7.1.css")}
            rel="stylesheet"
          />
        </NextHead>

        {!isLoading && (
          <Box
            className={`${isGoogleTranslateEnabled} ${isEmbedLayout}`}
            width={{ base: "70vw", lg: "70vw", sm: "100vw" }}
            zIndex={99}
          >
            <AllAlert />
            <MapContainer
              style={mapContainerStyle}
              ref={setMapRef}
              zoom={zoom}
              maxZoom={20}
              maxBoundsViscosity={10}
              minZoom={5}
              center={{ lat, lng }}
              zoomControl={false}
              bounceAtZoomLimits={true}
              maxBounds={[
                [90, 180],
                [-90, -180]
              ]}
            >
              {showInteractions && (
                <>
                  <ZoomControl position="topleft" />
                  <LocateControl
                    position="topleft"
                    strings={{
                      title: `${translate("places.pin.searchCurrentLocation")}`,

                      popup: `${translate(
                        `places.pin.withinLocation`
                      )} {distance} ${translate(
                        `places.pin.awayFromLocation`
                      )}`,
                      outsideMapBoundsMsg: `${translate(
                        "places.pin.locatedOutside"
                      )}`
                    }}
                  />
                  <EsriLeafletGeoSearchWithFocus
                    innerRef={geosearchControlRef}
                    position="topleft"
                    useMapBounds={false}
                    placeholder={`${translate("common.search")}`}
                    providers={{
                      arcgisOnlineProvider: {
                        apikey: process.env.NEXT_PUBLIC_ESRI_ACCESS_TOKEN
                      }
                    }}
                    key={process.env.NEXT_PUBLIC_ESRI_ACCESS_TOKEN}
                    title={`${translate("places.pin.searchLocation")}`}
                  />
                </>
              )}
              {marker.id === "new" && <NewMarker />}
              <VectorBasemapLayer
                ref={basemapLayerRef}
                name={mapName || "arcgis/streets"}
                token={esriToken}
                language={languages[locale]}
                version={2}
              />
              {rightPanelView === "view-pin-details" &&
              pinSearchFromUrl &&
              !isEmpty(pinDetails) ? (
                <CustomMarkers
                  viewMarkerClickHandler={viewMarkerClickHandler}
                  key={1}
                  pinDetail={pinDetails}
                ></CustomMarkers>
              ) : null}
              (
              <MarkerClusterGroup
                chunkedLoading
                spiderLegPolylineOptions={{ weight: 0 }}
                maxClusterRadius={250}
                disableClusteringAtZoom={17}
                spiderfyOnMaxZoom={false}
                chunkInterval={1000}
                chunkDelay={100}
                key={Date.now()}
                showCoverageOnHover
                removeOutsideVisibleBounds
              >
                {!isEmpty(pins) && rightPanelView !== "create-pin"
                  ? pins?.map((pin, index) => (
                      <CustomMarkers
                        viewMarkerClickHandler={viewMarkerClickHandler}
                        key={index}
                        pinDetail={pin}
                      ></CustomMarkers>
                    ))
                  : null}
              </MarkerClusterGroup>
              )
              <Layers />
              <SetViewOnClick />
            </MapContainer>
          </Box>
        )}
      </>
    );
  }, [
    isLoading,
    pins,
    marker,
    showInteractions,
    mapRef,
    viewPinDetails,
    googleTranslationEnabled,
    isEditMode,
    pinDetails
  ]);

  return mapComponent();
};

export default EsriMap;
